// ─── Chi può fare cosa ───────────────────────────────────────────────────────
//
// Sta in `shared` perché la stessa lista serve a tre posti: la guardia del
// server, le spunte della schermata Utenti staff e il vincolo `CHECK` del
// database (migrazione `…_staff.cjs`). Tre copie scritte a mano divergono; la
// terza non può importare TypeScript, quindi resta l'unica copia — e la prova
// `permessi.test.ts` la confronta riga per riga con questo file.
import { z } from 'zod';
/**
 * I cinque ruoli. `owner` e `admin` scavalcano la lista dei permessi; `maestro`
 * non ne riceve nessuno, mai (vedi `puo()` in fondo).
 *
 * ⚠️ L'elenco vive anche nel `CHECK` di `staff_users.role`: la copia nel
 * database sta in `migrations/1758330000000_maestri_entrano.cjs`, ed è quella
 * che `permessi.test.ts` confronta con questa.
 */
export const RUOLI = ['owner', 'admin', 'staff', 'readonly', 'maestro'];
export const ruoloSchema = z.enum(RUOLI);
/**
 * Il ruolo come lo legge chi lo guarda.
 *
 * ⚖️ `owner` è un codice, non una parola: sta bene in una colonna del database
 * e non sta bene sotto il nome del circolo. Le etichette vivono qui e non nella
 * schermata perché il ruolo si mostra in più posti, e due traduzioni diverse
 * della stessa parola fanno credere che siano due cose diverse.
 */
export const ETICHETTE_RUOLO = {
    owner: 'Proprietario',
    admin: 'Amministratore',
    staff: 'Operatore',
    readonly: 'Sola lettura',
    maestro: 'Maestro',
};
/**
 * I capitoli e le loro sottosezioni.
 *
 * 🩹 QUESTA È LA CURA DEL DIFETTO DEL § 01.2. Nel gestionale di oggi spuntare
 * un capitolo NON attiva le sue sottosezioni: il committente ha dovuto metterle
 * a mano una per una. Qui il legame è dichiarato, sta in un posto solo, e vale
 * nei DUE versi:
 *   · concedere un capitolo concede le sue sottosezioni;
 *   · togliere un capitolo le toglie tutte.
 * ⇒ Una sottosezione senza il suo capitolo non può esistere: sarebbe un
 *   permesso su una schermata a cui non si arriva.
 */
export const CAPITOLI = [
    { capitolo: 'view_dashboard', titolo: 'Calendario', sottosezioni: [] },
    {
        capitolo: 'view_members',
        titolo: 'Soci',
        sottosezioni: ['view_members_anagrafica', 'view_members_attivita', 'view_members_borsellino'],
    },
    { capitolo: 'view_incassi', titolo: 'Cassa', sottosezioni: ['manage_payments'] },
    {
        capitolo: 'view_administration',
        titolo: 'Impostazioni',
        sottosezioni: [
            'view_admin_utenti',
            'manage_users',
            'view_admin_notifiche',
            'view_admin_telegram',
            'manage_settings',
        ],
    },
];
/**
 * I 13 permessi, nell'ordine dei capitoli.
 *
 * ⛔ Non ci sono `view_admin_matchpoint`, `view_admin_supabase`, le cinque
 * `view_assessment_*` né `view_members_autoval`: cadono col perimetro. Un
 * permesso che non protegge niente è una spunta che qualcuno, un giorno, crede
 * significhi qualcosa.
 *
 * 🔄 **E dal 18/09/2026 non c'è più nemmeno `view_assistente_ai`**, per la
 * stessa ragione e non per una nuova: 🗣️ *«per quanto riguarda la sezione
 * assistente, possiamo eliminarla perché non serve più»*. Tolta la schermata,
 * quel permesso proteggeva il vuoto.
 * ⚠️ Toglierlo di qui **non basta**: il `CHECK` di `staff_permissions` è una
 * seconda copia della lista, e le due le tiene d'accordo `permessi.test.ts`.
 * ⇒ La migrazione `1758310000000_assistente_via.cjs` è la metà che vive nel
 * database.
 */
export const PERMESSI = CAPITOLI.flatMap((c) => [c.capitolo, ...c.sottosezioni]);
export const permessoSchema = z.enum(PERMESSI);
/** Le etichette che legge la segreteria. Un codice nudo non si spunta. */
export const ETICHETTE = {
    view_dashboard: 'Vedere il calendario',
    view_members: 'Vedere i soci',
    view_members_anagrafica: 'Anagrafica',
    view_members_attivita: 'Attività',
    view_members_borsellino: 'Borsellino',
    view_incassi: 'Vedere gli incassi',
    manage_payments: 'Incassare e stornare',
    view_administration: 'Vedere le impostazioni',
    view_admin_utenti: 'Utenti staff',
    manage_users: 'Modificare gli utenti staff',
    view_admin_notifiche: 'Notifiche staff',
    /** 🔄 28/09/2026: era «Rubrica Telegram», che non c'è più. Il permesso resta, e apre le Segnalazioni. */
    view_admin_telegram: 'Segnalazioni',
    manage_settings: 'Modificare le impostazioni',
};
const CAPITOLO_DI = new Map(CAPITOLI.flatMap((c) => c.sottosezioni.map((s) => [s, c.capitolo])));
/** Il capitolo a cui appartiene una sottosezione, o `null` se è già un capitolo. */
export function capitoloDi(permesso) {
    return CAPITOLO_DI.get(permesso) ?? null;
}
export function eUnCapitolo(permesso) {
    return CAPITOLI.some((c) => c.capitolo === permesso);
}
/**
 * Mette in ordine una scelta di spunte prima di scriverla.
 *
 * Due regole, e sono l'una il rovescio dell'altra:
 *   ① una sottosezione tira dentro il suo capitolo — senza, sarebbe un permesso
 *     su una schermata a cui non si arriva;
 *   ② un permesso che non è nell'elenco si SCARTA, non si scrive. Il `CHECK`
 *     del database lo rifiuterebbe comunque: meglio un elenco pulito che un
 *     errore di vincolo davanti a chi sta spuntando delle caselle.
 *
 * ⚖️ La regola «togliere un capitolo toglie le sottosezioni» non sta qui ma in
 * chi chiama: la schermata manda l'elenco INTERO delle spunte, non una
 * differenza, quindi togliere il capitolo significa non mandare le sue
 * sottosezioni. Vedi `togliCapitolo()` per il verso esplicito.
 */
export function normalizzaPermessi(scelti) {
    const dentro = new Set();
    for (const p of scelti) {
        if (!PERMESSI.includes(p))
            continue;
        const permesso = p;
        dentro.add(permesso);
        const capitolo = capitoloDi(permesso);
        if (capitolo)
            dentro.add(capitolo);
    }
    // L'ordine è quello dei capitoli, non quello in cui sono arrivati: due elenchi
    // uguali devono confrontarsi uguali.
    return PERMESSI.filter((p) => dentro.has(p));
}
/** Concede un capitolo INTERO: il capitolo e tutte le sue sottosezioni. */
export function concediCapitolo(attuali, capitolo) {
    const voce = CAPITOLI.find((c) => c.capitolo === capitolo);
    return normalizzaPermessi([...attuali, capitolo, ...(voce?.sottosezioni ?? [])]);
}
/** Toglie un capitolo e con lui tutte le sue sottosezioni. */
export function togliCapitolo(attuali, capitolo) {
    const voce = CAPITOLI.find((c) => c.capitolo === capitolo);
    const fuori = new Set([capitolo, ...(voce?.sottosezioni ?? [])]);
    return normalizzaPermessi([...attuali].filter((p) => !fuori.has(p)));
}
/**
 * ⭐ LA FUNZIONE CHE DECIDE, E NE ESISTE UNA SOLA.
 *
 * ⚠️ La riga da non ripetere: nel gestionale di oggi `pmoIsReadonlyStaff()`
 * risponde «no, non è readonly» anche quando il profilo NON C'È AFFATTO —
 * cioè dice «può» proprio nel caso in cui non si è nessuno. Qui il chiamante
 * non ha nessun modo di passare «nessuno»: `utente` è obbligatorio, e chi non
 * ha una riga in `staff_users` non arriva mai fin qui.
 */
export function puo(utente, permesso) {
    /*
     * ⛔⭐⭐ IL MAESTRO NON PASSA DI QUI, E LA RIGA STA PRIMA DI TUTTE.
     *
     * 🚨 **La misura da cui nasce**: tutte e tredici le rotte del calendario
     * dichiarano lo STESSO permesso, `view_dashboard` — creare, modificare,
     * annullare, aggiungere e togliere giocatori. ⇒ Non esiste nessuna
     * combinazione di spunte che dia a un maestro «vedi le tue lezioni» senza
     * dargli anche «annulla le partite di chiunque».
     * ⚖️ Quindi il maestro non passa dai permessi: passa da una **porta sua**
     *    (`permesso: 'maestro'` in `plugins/autenticazione.ts`), dove ogni query
     *    porta il suo id nella `where`. Le spunte restano il vocabolario della
     *    segreteria, e per lui dicono tutte **no**.
     * 🔒 E il `return false` è quello che rende **impossibile** l'incidente, non
     *    improbabile: chi spuntasse «Vedere il calendario» a un maestro — per
     *    distrazione, da una console, con un travaso — non gli consegnerebbe
     *    niente. Una difesa che poggia sull'attenzione di qualcuno non è una
     *    difesa: è un'abitudine.
     * 📌 *Il posto giusto per chiudere una porta è quello in cui si decide, non
     *    quello in cui si disegna il bottone.*
     */
    if (utente.ruolo === 'maestro')
        return false;
    // ⚖️ `owner` e `admin` scavalcano la lista. Resta com'è oggi, è deliberato, e
    // sta scritto QUI e in nessun altro punto del codice.
    if (utente.ruolo === 'owner' || utente.ruolo === 'admin')
        return true;
    return utente.permessi.includes(permesso);
}
//# sourceMappingURL=permessi.js.map